/**
 * TaskCard — the primary unit of the tasks board.
 *
 * Renders a single ServiceRequest: room, category, description, urgency, status,
 * team, source — every status/urgency shown with a TEXT label (never colour
 * alone). High-urgency tasks carry a supplementary left border. Active tasks
 * expose status actions + a non-optimistic escalate flow.
 */

import type { ServiceRequest } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import { Card, CardContent, cn, StatusBadge } from '@hostesa/ui';
import { EscalateDialog } from './escalate-dialog';
import { RoomStatusBadge } from './room-status-badge';
import { asOptionalString, isActive, statusVariant, urgencyVariant } from './task-presentation';
import { TaskStatusControl } from './task-status-control';

export interface TaskCardProps {
  task: ServiceRequest;
  tenantId: string;
  hotelId: string;
}

export function TaskCard({ task, tenantId, hotelId }: TaskCardProps) {
  const { t } = useT('operations');

  const team = asOptionalString(task.assignedTeam);
  const active = isActive(task.status);
  const highUrgency = task.urgency === 'high';

  return (
    <Card
      data-testid={`task-card-${task.id}`}
      className={cn(highUrgency && 'border-l-2 border-l-[var(--color-warm-stone-deep)]')}
    >
      <CardContent className="flex flex-col gap-3 pt-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <RoomStatusBadge room={task.room} data-testid={`task-card-room-${task.id}`} />
          <span className="text-xs text-[var(--color-stone-mid)]">
            {t(`category.${task.category}`)}
          </span>
        </div>

        <p className="text-sm text-[var(--color-charcoal)] leading-relaxed">{task.description}</p>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <StatusBadge
            status={statusVariant(task.status)}
            label={t(`status.${task.status}`)}
            data-testid={`task-card-status-${task.id}`}
          />
          <StatusBadge
            status={urgencyVariant(task.urgency)}
            label={`${t('urgency.label')}: ${t(`urgency.${task.urgency}`)}`}
            data-testid={`task-card-urgency-${task.id}`}
          />
          <span className="text-xs text-[var(--color-stone-mid)]">
            {t('card.team')}: {team ?? t('card.unassigned')}
          </span>
          <span className="text-xs text-[var(--color-stone-mid)]">
            {t('card.source')}: {t(`source.${task.sourceChannel}`)}
          </span>
        </div>

        {task.status === 'escalated' && (
          <p
            className="text-xs text-[var(--color-alert)]"
            data-testid={`task-card-escalated-note-${task.id}`}
          >
            {t('card.escalatedNote')}
          </p>
        )}

        {active && (
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <TaskStatusControl task={task} tenantId={tenantId} hotelId={hotelId} />
            <EscalateDialog task={task} tenantId={tenantId} hotelId={hotelId} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
